import { ApiError, api } from '../../lib/api'
import { upload } from '../../lib/upload'
import { strings as S } from './strings'

/**
 * Install console — `Features/Admin/Install/Endpoint.cs`.
 *
 *   getInstallState   -> GET  /api/install              -> { IsArabic, IsAccessGranted }
 *   grantAccess       -> POST /Install/GrantAccess      { Passkey }        -> 200 | 400
 *   applyLicense      -> POST /Install/Apply            multipart: fUldLicense   -> HTML callback
 *   fakeDataInsertion -> POST /Install/FakeDataInsertion { UsersCount }    -> { Message, Result }
 *   generateMaterial  -> POST /Install/GenerateMaterial { TopicsOnly }     -> { Message, Result }
 *   usersPurge        -> POST /Install/UsersPurge       multipart: users   -> HTML callback
 *
 * THE PASSKEY IS NO LONGER IN THIS FILE. The mock compared it in the browser against a
 * constant compiled into the bundle, which meant the install passkey shipped to every
 * visitor and the gate could be stepped over from a console. The server has always done
 * this properly — it checks the passkey and answers with a data-protected, HttpOnly
 * cookie good for fifteen minutes — so the gate is simply asked rather than imitated.
 *
 * TWO OF THESE ANSWER HTML. `Apply` and `UsersPurge` reply with
 * `window.parent.finishUpload(bool,'…')` in a `text/html` body — the iframe-upload
 * convention from the jQuery era. `src/lib/upload.js` parses that reply (it never
 * executes it), so the shape below is the same as the JSON actions'.
 */

/** Whether the passkey has already been accepted — the SERVER's answer, not a guess. */
export async function getInstallState(signal) {
  try {
    const dto = await api.get('/api/install', { signal })
    return { accessGranted: Boolean(dto?.IsAccessGranted) }
  } catch (error) {
    if (error instanceof ApiError) return { accessGranted: false }
    throw error
  }
}

export async function grantAccess(passkey) {
  try {
    await api.post('/Install/GrantAccess', { Passkey: passkey })
    // 200 means the cookie is set; nothing here needs to remember that.
    return { result: true, message: '' }
  } catch {
    /* A wrong passkey is a 400. No message: the gate says "that is not it" and nothing
       more, because a wrong-passkey screen that elaborates is helping the wrong person. */
    return { result: false, message: '' }
  }
}

export async function applyLicense(file) {
  if (!file || file.size === 0) {
    return { result: false, message: S.messages.invalidFiles }
  }

  const form = new FormData()
  form.append('fUldLicense', file)

  return run(() => upload('/Install/Apply', form))
}

export async function fakeDataInsertion(usersCount) {
  return run(() => api.post('/Install/FakeDataInsertion', { UsersCount: usersCount }))
}

export async function generateMaterial() {
  // The command has a `TopicsOnly` flag and this screen is the topics-only button.
  return run(() => api.post('/Install/GenerateMaterial', { TopicsOnly: true }))
}

export async function usersPurge(file) {
  if (!file || file.size === 0) {
    return { result: false, message: S.messages.cannotOpenFile }
  }

  const form = new FormData()
  form.append('users', file)

  return run(() => upload('/Install/UsersPurge', form))
}

/**
 * Every action on this screen reports the same way: a sentence and whether it worked.
 *
 * These are slow, destructive operations, so the server's own words are always preferred
 * — "3,000 users inserted" or the row that broke says far more than "done".
 */
async function run(action) {
  try {
    const data = await action()
    return { result: true, message: data?.Message || S.messages.success }
  } catch (error) {
    if (error instanceof ApiError) {
      return { result: false, message: error.message || S.messages.invalidFiles }
    }
    throw error
  }
}
